import {
  ChangeEvent,
  HTMLAttributes,
  KeyboardEvent,
  MouseEvent,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { valueForOnlyNumericInputTextFields, valueForOnlyStringInputTextFields } from "utils/helpers";

import { SUB_CUSTOMER_OPTIONS } from "app/containers/Quotes/data";
import { quotesActions } from "app/containers/Quotes/redux/slice";
import {
  VEHICLE_BOX_TYPE_OPTIONS,
  VEHICLE_REAR_SUSPENSION_OPTIONS,
} from "app/containers/WorkOrder/types";
import { actions as workOrderActions } from "app/containers/WorkOrder/redux/slice";
import {
  selectAddOrderDropDowns,
  selectAddOrderForm,
  selectAddOrderModalContext,
  selectIsAddOrderModalOpen,
} from "app/containers/CommonModal/redux/selector";
import { addOrderModalActions } from "app/containers/CommonModal/redux/slice";
import {
  AddOrderDropDownKey,
  AddOrderForm,
  AddOrderModalContext,
  AddOrderSelectOption,
} from "app/containers/CommonModal/types";

export type OrderFieldConfig = {
  key: keyof AddOrderForm;
  label: string;
  type: "select" | "text";
  options?: AddOrderSelectOption[];
  inputMode?: HTMLAttributes<HTMLInputElement>["inputMode"];
  loading?: boolean;
  disabled?: boolean;
  dropDownKey?: AddOrderDropDownKey;
};

const FORM_I18N_PREFIX = "QUOTES.MODAL";
const MAX_IMAGE_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_IMAGE_TYPES = ["image/png", "image/jpeg"];

const REQUIRED_FIELDS: Array<keyof AddOrderForm> = [
  "customerName",
  "chassis",
  "manufacturer",
  "model",
  "axleConfiguration",
  "year",
  "boxType",
];

const AXLE_YEAR_FIELDS: OrderFieldConfig[] = [
  {
    key: "axleConfiguration",
    label: "AXLE_CONFIGURATION",
    type: "select",
    options: VEHICLE_REAR_SUSPENSION_OPTIONS.map(([value, labelKey]) => ({
      value,
      labelKey,
    })),
  },
  {
    key: "year",
    label: "YEAR",
    type: "text",
    inputMode: "numeric",
  },
];

const BOX_TYPE_FIELD: OrderFieldConfig = {
  key: "boxType",
  label: "BOX_TYPE",
  type: "select",
  options: VEHICLE_BOX_TYPE_OPTIONS.map(([value, labelKey]) => ({
    value,
    labelKey,
  })),
};





const isAcceptedImage = (file: File) =>
  ACCEPTED_IMAGE_TYPES.includes(file.type) || /\.(png|jpe?g)$/i.test(file.name);

export const useAddOrderModal = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [imageError, setImageError] = useState("");

  const open = useSelector(selectIsAddOrderModalOpen);
  const form = useSelector(selectAddOrderForm);
  const context = useSelector(selectAddOrderModalContext);
  const dropDowns = useSelector(selectAddOrderDropDowns);

  const customerNames = dropDowns.customers.items;
  const manufacturerNames = dropDowns.manufacturers.items;
  const modelNames = dropDowns.models.items;



  const isValid = REQUIRED_FIELDS.every((key) => Boolean(form[key].trim()));

  const fieldLabel = (label: string) => t(`${FORM_I18N_PREFIX}.FIELDS.${label}`);

  const placeholder = (label: string, type: "select" | "text") =>
    t(
      `${FORM_I18N_PREFIX}.PLACEHOLDERS.${
        type === "select" ? "SELECT" : "ENTER"
      }_${label}`
    );

  const optionLabel = (option: AddOrderSelectOption) =>
    option.labelKey ? t(option.labelKey) : option.label || option.value;

  const setField = (name: keyof AddOrderForm, value: string) =>
    dispatch(addOrderModalActions.setFormField({ name, value }));

  const onFieldChange = (key: keyof AddOrderForm, value: string) => {
    const nextValue =
      key === "year"
        ? valueForOnlyNumericInputTextFields(value).slice(0, 4)
        : key === "chassis"
          ? valueForOnlyStringInputTextFields(value)
          : value;
    setField(key, nextValue);
  };

  const onClose = () => {
    setImageError("");
    dispatch(addOrderModalActions.closeAddOrderModal());
  };

  const onSubmit = () => {
    if (context === AddOrderModalContext.QUOTE) {
      dispatch(quotesActions.createQuoteFromForm(form));
    } else {
      dispatch(workOrderActions.createWorkOrderFromForm(form));
    }

    dispatch(addOrderModalActions.closeAddOrderModal());
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const clearImage = (event: MouseEvent) => {
    event.stopPropagation();
    setImageError("");
    setField("vehicleImage", "");
    setField("vehicleImageName", "");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) {
      return;
    }

    if (!isAcceptedImage(file)) {
      setImageError(t(`${FORM_I18N_PREFIX}.IMAGE_ERRORS.TYPE`));
      event.target.value = "";
      return;
    }

    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      setImageError(t(`${FORM_I18N_PREFIX}.IMAGE_ERRORS.SIZE`));
      event.target.value = "";
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      setImageError("");
      setField("vehicleImage", String(reader.result || ""));
      setField("vehicleImageName", file.name);
    };
    reader.readAsDataURL(file);
  };

  const onUploadKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openFilePicker();
    }
  };

  const onDropDownScroll = (dropDownKey?: AddOrderDropDownKey) => {
    if (!dropDownKey) return;
    dispatch(addOrderModalActions.getDropDownNextPage(dropDownKey));
  };

  const hasMoreDropDownItems = (dropDownKey?: AddOrderDropDownKey) => {
    if (!dropDownKey) return false;
    const dropDown = dropDowns[dropDownKey];
    return dropDown.meta.currentPage < dropDown.meta.totalPages;
  };

  return {
    t,
    open,
    form,
    isValid,
    imageError,
    fileInputRef,
    customerNames,
    manufacturerNames,
    modelNames,
    subCustomerOptions: SUB_CUSTOMER_OPTIONS,
    axleYearFields: AXLE_YEAR_FIELDS,
    boxTypeField: BOX_TYPE_FIELD,
    fieldLabel,
    placeholder,
    optionLabel,
    onFieldChange,
    onClose,
    onSubmit,
    openFilePicker,
    clearImage,
    onFileChange,
    onUploadKeyDown,
    onDropDownScroll,
    hasMoreDropDownItems,
    uploadLabel: t(`${FORM_I18N_PREFIX}.UPLOAD`),
    uploadHint: t(`${FORM_I18N_PREFIX}.UPLOAD_HINT`),
    cancelLabel: t("BUTTONS.CANCEL"),
  };
};
